import clsx from "clsx";
import MarkdownIt, { type Token } from "markdown-it";
import { Fragment, useMemo, type ReactNode } from "react";

/*
  Text from the model, notes and chapters is shown through this component (architecture 15.6).

  - Raw HTML is off: markdown-it is started with `html: false`, and the text is never put into the
    page as markup. The tokens are turned into React elements here, one allowed kind at a time, so
    no raw HTML is ever inserted (the lint rule `react/no-danger` is an error).
  - Links and images are removed: the words of a link stay, an image shows only its description.
    Nothing in the answer can make the browser fetch an address.
*/

const md = new MarkdownIt("zero", { html: false, linkify: false, typographer: false, breaks: false, maxNesting: 20 });
md.enable(
  [
    "normalize",
    "block",
    "inline",
    "text_join",
    "paragraph",
    "heading",
    "lheading",
    "list",
    "blockquote",
    "fence",
    "code",
    "hr",
    "reference",
    "text",
    "newline",
    "escape",
    "backticks",
    "emphasis",
    "strikethrough",
    "entity",
    "link",
    "image",
    "autolink",
    "balance_pairs",
    "fragments_join",
  ],
  true,
);

/** A long answer is cut here, not parsed whole. */
const MAX_CHARS = 60_000;

function renderLeaf(token: Token, key: string): ReactNode {
  switch (token.type) {
    case "inline":
      return <Fragment key={key}>{renderTokens(token.children ?? [], key)}</Fragment>;
    case "text":
      return token.content;
    case "softbreak":
      return "\n";
    case "hardbreak":
      return <br key={key} />;
    case "code_inline":
      return <code key={key}>{token.content}</code>;
    case "fence":
    case "code_block":
      return (
        <pre key={key}>
          <code>{token.content}</code>
        </pre>
      );
    case "hr":
      return <hr key={key} />;
    case "image":
      return token.content; // the description only
    default:
      return token.content; // anything unexpected is still only text
  }
}

function renderContainer(open: Token, inner: Token[], key: string): ReactNode {
  const children = renderTokens(inner, key);
  switch (open.type) {
    case "paragraph_open":
      return open.hidden ? <Fragment key={key}>{children}</Fragment> : <p key={key}>{children}</p>;
    case "heading_open":
      // A heading inside an answer must not change the outline of the page.
      return (
        <p key={key} className="safe-markdown__heading">
          <strong>{children}</strong>
        </p>
      );
    case "bullet_list_open":
      return <ul key={key}>{children}</ul>;
    case "ordered_list_open": {
      const start = Number(open.attrGet("start") ?? 1);
      return (
        <ol key={key} start={Number.isFinite(start) && start > 0 ? start : 1}>
          {children}
        </ol>
      );
    }
    case "list_item_open":
      return <li key={key}>{children}</li>;
    case "blockquote_open":
      return <blockquote key={key}>{children}</blockquote>;
    case "strong_open":
      return <strong key={key}>{children}</strong>;
    case "em_open":
      return <em key={key}>{children}</em>;
    case "s_open":
      return <s key={key}>{children}</s>;
    default:
      // link_open and anything else: the words stay, the wrapper goes.
      return <Fragment key={key}>{children}</Fragment>;
  }
}

function renderTokens(tokens: Token[], keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let i = 0;
  while (i < tokens.length) {
    const token = tokens[i]!;
    const key = `${keyBase}.${i}`;
    if (token.nesting === 1) {
      let depth = 0;
      let j = i;
      for (; j < tokens.length; j += 1) {
        depth += tokens[j]!.nesting;
        if (depth === 0) break;
      }
      out.push(renderContainer(token, tokens.slice(i + 1, j), key));
      i = j + 1;
    } else {
      out.push(renderLeaf(token, key));
      i += 1;
    }
  }
  return out;
}

/** Render Markdown as plain, safe elements. */
export function SafeMarkdown({ children, className }: { children: string; className?: string }) {
  const nodes = useMemo(() => {
    const text = children.length > MAX_CHARS ? `${children.slice(0, MAX_CHARS)}\n\n(The rest of this text is not shown.)` : children;
    return renderTokens(md.parse(text, {}), "md");
  }, [children]);
  return <div className={clsx("safe-markdown", className)}>{nodes}</div>;
}
